import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { IFavorite, TFavoriteEntityType } from "@/types/favorite";

export class FavoriteStore {
  favoriteMap = observable.map<string, IFavorite>();
  entityMap = observable.map<string, IFavorite>();
  favoriteIds: string[] = [];
  isLoading = false;
  /** Favorites and folders with a rename, move or delete in flight (NG-2658).
   *  Their rows are busy until the server answers, so a settled row means the
   *  change saved. */
  savingIds = observable.set<string>();

  private rootStore: RootStore;

  /** Creates still in flight, by entity, with the temp id of the optimistic
   *  entry each one stands behind. Until one answers, that id means nothing to
   *  the server, so a remove has to wait for the real one (NG-2069). */
  private pendingCreates = new Map<string, { tempId: string; create: Promise<IFavorite> }>();
  /** Temp ids a remove took out while their create was in flight. The create
   *  must not put them back: the remove deletes the real favorite itself. */
  private removedWhilePending = new Set<string>();

  /** A logical clock over local writes. The list `fetchFavorites` loads is the
   *  server's state as of the request, and the workspace loader re-fetches it
   *  on a workspace switch or an explicit revalidation (NG-2410), so its answer
   *  can land after a star or an unstar it predates. Replacing the map with that answer
   *  erased a star that had already saved, and would restore one already removed
   *  (NG-2069, which flaked intake/request-favorites.spec.ts). Each local add and
   *  remove is stamped, and a fetch keeps the writes stamped after it started. */
  private clock = 0;
  private addedAt = new Map<string, number>();
  private removedAt = new Map<string, number>();
  private fetchesInFlight = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        pendingCreates: false,
        removedWhilePending: false,
        clock: false,
        addedAt: false,
        removedAt: false,
        fetchesInFlight: false,
      } as never,
      { autoBind: true },
    );
  }

  private stampAdded(favoriteId: string): void {
    this.addedAt.set(favoriteId, ++this.clock);
  }

  private stampRemoved(favoriteId: string): void {
    this.removedAt.set(favoriteId, ++this.clock);
  }

  /**
   * Root-level favorites (no parent) sorted by sequence descending.
   */
  get rootFavorites(): IFavorite[] {
    return Array.from(this.favoriteMap.values())
      .filter((f) => !f.parent)
      .sort((a, b) => b.sequence - a.sequence);
  }

  /**
   * Folders only (root-level).
   */
  get folders(): IFavorite[] {
    return this.rootFavorites.filter((f) => f.is_folder);
  }

  isSaving(favoriteId: string): boolean {
    return this.savingIds.has(favoriteId);
  }

  /**
   * Check if an entity is favorited.
   */
  isEntityFavorited(entityIdentifier: string): boolean {
    return this.entityMap.has(entityIdentifier);
  }

  /**
   * Get favorite by entity identifier.
   */
  getFavoriteByEntity(entityIdentifier: string): IFavorite | undefined {
    return this.entityMap.get(entityIdentifier);
  }

  removeFavoriteLocally(entityIdentifier: string): void {
    const favorite = this.entityMap.get(entityIdentifier);
    if (!favorite) return;

    this.entityMap.delete(entityIdentifier);
    this.favoriteMap.delete(favorite.id);
    this.favoriteIds = this.favoriteIds.filter((id) => id !== favorite.id);
  }

  /**
   * Get children of a folder from local map, sorted by sequence descending.
   */
  getFolderChildren(folderId: string): IFavorite[] {
    return Array.from(this.favoriteMap.values())
      .filter((f) => f.parent === folderId)
      .sort((a, b) => b.sequence - a.sequence);
  }

  /**
   * Fetch all root-level favorites.
   */
  async fetchFavorites(slug: string): Promise<void> {
    this.isLoading = true;
    const startedAt = this.clock;
    this.fetchesInFlight += 1;
    try {
      const favorites = await this.rootStore.favoriteService.fetchFavorites(slug);
      runInAction(() => {
        // Writes made after this request left are newer than its answer, and an
        // optimistic entry whose create is still in flight is never in it.
        const pendingTemps = new Set(Array.from(this.pendingCreates.values(), (p) => p.tempId));
        const keptLocal = Array.from(this.favoriteMap.values()).filter(
          (fav) => pendingTemps.has(fav.id) || (this.addedAt.get(fav.id) ?? 0) > startedAt,
        );
        const removedSince = new Set(
          Array.from(this.removedAt)
            .filter(([, at]) => at > startedAt)
            .map(([id]) => id),
        );
        const claimedEntities = new Set(
          keptLocal.map((fav) => fav.entity_identifier).filter((id): id is string => !!id),
        );

        this.favoriteMap.clear();
        this.entityMap.clear();
        this.favoriteIds = [];
        const fromServer = favorites.filter(
          (fav) =>
            !removedSince.has(fav.id) &&
            !(fav.entity_identifier && claimedEntities.has(fav.entity_identifier)),
        );
        [...keptLocal, ...fromServer].forEach((fav) => {
          if (this.favoriteMap.has(fav.id)) return;
          this.favoriteMap.set(fav.id, fav);
          this.favoriteIds.push(fav.id);
          if (fav.entity_identifier) {
            this.entityMap.set(fav.entity_identifier, fav);
          }
        });
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
      this.fetchesInFlight -= 1;
      if (this.fetchesInFlight === 0) {
        // No fetch can predate these any more.
        this.addedAt.clear();
        this.removedAt.clear();
      }
    }
  }

  /**
   * Add entity to favorites with optimistic update.
   */
  async addFavorite(
    slug: string,
    entityType: TFavoriteEntityType,
    entityIdentifier: string,
    projectId?: string,
    // Persisted display label. The API returns raw model fields only, so an
    // entity the sidebar can't resolve from a loaded store (e.g. a work item
    // on a cold load) renders from this instead of "Untitled".
    name?: string,
    source: AnalyticsSource = "page",
  ): Promise<IFavorite> {
    const existing = this.entityMap.get(entityIdentifier);
    if (existing) {
      return existing;
    }

    const tempId = `temp-${Date.now()}`;
    const tempFav: IFavorite = {
      id: tempId,
      entity_type: entityType,
      entity_identifier: entityIdentifier,
      entity_data: { name: name ?? "" },
      name: name ?? "",
      is_folder: false,
      sort_order: 0,
      sequence: Date.now(),
      parent: null,
      workspace_id: "",
      project_id: projectId ?? null,
      children: [],
    };

    const prevEntity = this.entityMap.get(entityIdentifier) ?? null;

    runInAction(() => {
      this.favoriteMap.set(tempId, tempFav);
      this.entityMap.set(entityIdentifier, tempFav);
      this.favoriteIds = [tempId, ...this.favoriteIds];
    });
    this.stampAdded(tempId);

    const create = this.rootStore.favoriteService.addFavorite(slug, {
      entity_type: entityType,
      entity_identifier: entityIdentifier,
      // `project` is the serializer's writable FK field; `project_id` was
      // silently dropped on create. Both are sent while readers migrate.
      project: projectId,
      project_id: projectId,
      name,
    });
    this.pendingCreates.set(entityIdentifier, { tempId, create });

    try {
      const response = await create;

      if (this.removedWhilePending.delete(tempId)) {
        // Unstarred before the create answered; that remove deletes `response`.
        return response;
      }
      runInAction(() => {
        this.favoriteMap.delete(tempId);
        this.favoriteMap.set(response.id, response);
        if (response.entity_identifier) {
          this.entityMap.set(response.entity_identifier, response);
        }
        this.favoriteIds = [response.id, ...this.favoriteIds.filter((id) => id !== tempId)];
      });
      this.stampAdded(response.id);
      track("favorite_added", { entity_type: entityType, favorite_id: response.id, source });

      return response;
    } catch (error) {
      const alreadyRemoved = this.removedWhilePending.delete(tempId);
      runInAction(() => {
        this.favoriteMap.delete(tempId);
        // A remove issued meanwhile already took the entry out; restoring the
        // previous entity would undo it.
        if (prevEntity && !alreadyRemoved) {
          this.entityMap.set(entityIdentifier, prevEntity);
        } else if (this.entityMap.get(entityIdentifier)?.id === tempId) {
          this.entityMap.delete(entityIdentifier);
        }
        this.favoriteIds = this.favoriteIds.filter((id) => id !== tempId);
      });
      throw error;
    } finally {
      // A later create for the same entity may own the slot by now.
      if (this.pendingCreates.get(entityIdentifier)?.create === create) {
        this.pendingCreates.delete(entityIdentifier);
      }
    }
  }

  /**
   * Remove entity from favorites with optimistic update.
   */
  async removeFavoriteByEntity(
    slug: string,
    entityIdentifier: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const fav = this.entityMap.get(entityIdentifier);
    if (!fav) return;

    let favId = fav.id;
    let backup = { ...fav };

    runInAction(() => {
      this.favoriteMap.delete(favId);
      this.entityMap.delete(entityIdentifier);
      this.favoriteIds = this.favoriteIds.filter((id) => id !== favId);
    });
    this.stampRemoved(favId);

    // Still the optimistic entry of a create in flight: its temp id means
    // nothing to the server, so wait for the create and delete what it made.
    // Matched on the create's own temp id, so nothing here depends on how
    // temp ids are spelled.
    const pending = this.pendingCreates.get(entityIdentifier);
    if (pending && pending.tempId === favId) {
      this.removedWhilePending.add(favId);
      let createdFav: IFavorite;
      try {
        createdFav = await pending.create;
      } catch (error: unknown) {
        // The create failed, and addFavorite reverted and rethrew it to its own
        // caller; there is nothing on the server to delete.
        console.warn("[FavoriteStore] create failed while a remove waited on it", {
          entityIdentifier,
          tempId: favId,
          error,
        });
        return;
      }
      favId = createdFav.id;
      backup = { ...createdFav };
      // A fetch that saw the created row must not bring it back either.
      this.stampRemoved(favId);
    }

    try {
      await this.rootStore.favoriteService.deleteFavorite(slug, favId);
      track("favorite_removed", { entity_type: backup.entity_type, favorite_id: favId, source });
    } catch (error) {
      runInAction(() => {
        this.favoriteMap.set(favId, backup);
        this.entityMap.set(entityIdentifier, backup);
        this.favoriteIds = [favId, ...this.favoriteIds];
      });
      this.removedAt.delete(favId);
      this.stampAdded(favId);
      throw error;
    }
  }

  /**
   * Create a new folder.
   */
  async createFolder(
    slug: string,
    name: string,
    source: AnalyticsSource = "page",
  ): Promise<IFavorite> {
    const response = await this.rootStore.favoriteService.addFavorite(slug, {
      entity_type: "folder",
      name,
      is_folder: true,
    });
    runInAction(() => {
      this.favoriteMap.set(response.id, response);
      this.favoriteIds = [response.id, ...this.favoriteIds];
    });
    this.stampAdded(response.id);
    track("favorite_folder_created", { folder_id: response.id, source });
    return response;
  }

  /**
   * Rename a folder with optimistic update.
   */
  async renameFolder(
    slug: string,
    folderId: string,
    name: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const prev = this.favoriteMap.get(folderId)?.name;
    runInAction(() => {
      const folder = this.favoriteMap.get(folderId);
      if (folder) folder.name = name;
      this.savingIds.add(folderId);
    });
    try {
      await this.rootStore.favoriteService.updateFavorite(slug, folderId, { name });
      track("favorite_folder_renamed", { folder_id: folderId, source });
    } catch (error) {
      console.error("[FavoriteStore] folder rename failed", { folderId, error });
      runInAction(() => {
        const folder = this.favoriteMap.get(folderId);
        if (folder) folder.name = prev ?? "";
      });
      throw new Error("Failed to rename folder");
    } finally {
      runInAction(() => this.savingIds.delete(folderId));
    }
  }

  /**
   * Delete a folder. Moves children to root first, then deletes.
   *
   * The folder stays, busy, until the server answers, and only then leaves the
   * list, so its absence means the delete saved (NG-2658). Rejects on failure
   * so the caller can say so: a folder that just reappeared told nobody why.
   */
  async deleteFolder(
    slug: string,
    folderId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const children = Array.from(this.favoriteMap.values()).filter((f) => f.parent === folderId);
    runInAction(() => this.savingIds.add(folderId));
    try {
      // Moved without their own events: emptying a folder is part of deleting it.
      const moved = await Promise.all(
        children.map((child) => this.applyMove(slug, child.id, null)),
      );
      // A child the server would not move is still inside: deleting the folder
      // now would take it along, so stop with the folder still there.
      if (moved.includes(false)) throw new Error("Failed to empty folder before deleting it");
      try {
        await this.rootStore.favoriteService.deleteFavorite(slug, folderId);
      } catch (error) {
        console.error("[FavoriteStore] folder delete failed", { folderId, error });
        throw error;
      }
      runInAction(() => {
        this.favoriteMap.delete(folderId);
        this.favoriteIds = this.favoriteIds.filter((id) => id !== folderId);
      });
      track("favorite_folder_deleted", { folder_id: folderId, count: children.length, source });
    } finally {
      runInAction(() => this.savingIds.delete(folderId));
    }
  }

  /**
   * Move a favorite into or out of a folder with optimistic update.
   */
  async moveFavorite(
    slug: string,
    favoriteId: string,
    parentId: string | null,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    runInAction(() => this.savingIds.add(favoriteId));
    let moved: boolean;
    try {
      moved = await this.applyMove(slug, favoriteId, parentId);
    } finally {
      runInAction(() => this.savingIds.delete(favoriteId));
    }
    // ``applyMove`` already logged and reverted; reject so the caller can say
    // so, rather than a favorite that silently jumps back (NG-2658).
    if (!moved) throw new Error("Failed to move favorite");
    track("favorite_moved", { favorite_id: favoriteId, into_folder: parentId !== null, source });
  }

  /** The move itself, optimistic, resolving to whether the server took it. */
  private async applyMove(
    slug: string,
    favoriteId: string,
    parentId: string | null,
  ): Promise<boolean> {
    const prev = this.favoriteMap.get(favoriteId)?.parent;
    runInAction(() => {
      const favorite = this.favoriteMap.get(favoriteId);
      if (favorite) favorite.parent = parentId;
    });
    try {
      await this.rootStore.favoriteService.updateFavorite(slug, favoriteId, { parent: parentId });
      return true;
    } catch (error) {
      console.error("[FavoriteStore] move failed", { favoriteId, parentId, error });
      runInAction(() => {
        const favorite = this.favoriteMap.get(favoriteId);
        if (favorite) favorite.parent = prev ?? null;
      });
      return false;
    }
  }

  /**
   * Reorder a favorite using midpoint sequence calculation.
   */
  async reorderFavorite(
    slug: string,
    favoriteId: string,
    targetId: string,
    position: "above" | "below",
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const sorted = this.rootFavorites;
    const targetIndex = sorted.findIndex((f) => f.id === targetId);
    if (targetIndex === -1) return;

    let newSequence: number;
    if (position === "above") {
      const prevItem = sorted[targetIndex - 1];
      const targetSeq = sorted[targetIndex].sequence;
      newSequence = prevItem ? (targetSeq + prevItem.sequence) / 2 : targetSeq + 10000;
    } else {
      const nextItem = sorted[targetIndex + 1];
      const targetSeq = sorted[targetIndex].sequence;
      newSequence = nextItem ? (targetSeq + nextItem.sequence) / 2 : targetSeq - 10000;
    }

    const prevSequence = this.favoriteMap.get(favoriteId)?.sequence;
    runInAction(() => {
      const favorite = this.favoriteMap.get(favoriteId);
      if (favorite) {
        favorite.sequence = newSequence;
      }
    });

    try {
      await this.rootStore.favoriteService.updateFavorite(slug, favoriteId, {
        sequence: newSequence,
      });
      track("favorite_reordered", { favorite_id: favoriteId, source });
    } catch (error) {
      console.error("[FavoriteStore] reorder failed", { favoriteId, error });
      runInAction(() => {
        const favorite = this.favoriteMap.get(favoriteId);
        if (favorite && prevSequence !== undefined) {
          favorite.sequence = prevSequence;
        }
      });
    }
  }

  /**
   * Fetch children of a folder.
   */
  async fetchGroupedFavorites(slug: string, folderId: string): Promise<IFavorite[]> {
    const children = await this.rootStore.favoriteService.fetchGroupedFavorites(slug, folderId);
    runInAction(() => {
      children.forEach((child) => {
        this.favoriteMap.set(child.id, child);
        if (child.entity_identifier) {
          this.entityMap.set(child.entity_identifier, child);
        }
        if (!this.favoriteIds.includes(child.id)) {
          this.favoriteIds.push(child.id);
        }
      });
    });
    return children;
  }
}
